import { PageLayoutDto, UserConfigDto } from "@lib/ShiOneClient";
import {
  useGetUserConfig,
  UserConfigResponse,
  useUpsertUserConfig,
} from "../../../../modules/hooks/useUserConfig";
import { useOverlayHostPathname } from "shared-ui/src/hooks/routedOverlays";
import { useCallback, useEffect, useState } from "react";
import type { PageLayoutWidgetFilter } from "shared-ui/src/dashboards/models/PageLayoutWidgetFilter";

function getUserConfigWidgetFilterValueIfExists<T>(
  userConfig: UserConfigDto | undefined,
  isLoading: boolean,
  initialValue: T
): T {
  if (userConfig?.settingsValue && !isLoading) {
    const widgetFilter = JSON.parse(userConfig.settingsValue);
    return widgetFilter.filterValue ?? initialValue;
  }
  return initialValue;
}

export function getColumnWidgetsFromPage(
  page: PageLayoutDto,
  position: number
) {
  if (page.columns && page.columns.length > position) {
    return page.columns[position];
  }
  return undefined;
}

export function useWidgetFilterUserConfig<T>(
  widgetId: string,
  filterKey: string,
  initialValue: T
) {
  const filterUserConfig = useGetWidgetFilterUserConfig(widgetId);

  return {
    ...useSetUserColumnWidgetFilterConfig(
      widgetId,
      filterKey,
      filterUserConfig,
      initialValue
    ),
    isLoadingFilterUserConfig: filterUserConfig.isLoading,
  };
}

export function useGetWidgetFilterUserConfig(
  widgetId: string | undefined
): UserConfigResponse {
  const hostPathname = useOverlayHostPathname();
  const settingsKey = `${hostPathname}-WidgetId:${widgetId}`;
  const userConfig = useGetUserConfig(settingsKey);
  return userConfig;
}

export function useSetUserColumnWidgetFilterConfig<T>(
  widgetId: string,
  filterKey: string,
  filterUserConfig: UserConfigResponse,
  initialFilterValue: T
) {
  const defaultFilterValue = getUserConfigWidgetFilterValueIfExists(
    filterUserConfig.userConfig,
    filterUserConfig.isLoading,
    initialFilterValue
  );

  const [currentFilterValue, setCurrentFilterValue] =
    useState<T>(defaultFilterValue);

  const hostPathname = useOverlayHostPathname();
  const upsertUserConfigs = useUpsertUserConfig();

  useEffect(() => {
    if (defaultFilterValue !== undefined && !filterUserConfig.isLoading) {
      setCurrentFilterValue(defaultFilterValue);
    }
  }, [defaultFilterValue, filterUserConfig.isLoading]);

  const setFilter = useCallback(
    (value: T) => {
      setCurrentFilterValue(value);

      const widgetFilter: Omit<PageLayoutWidgetFilter, "filterValue"> & { filterValue: T } = {
        widgetId,
        filterKey,
        filterValue: value,
      };

      const settings = new UserConfigDto();
      settings.settingsKey = `${hostPathname}-WidgetId:${widgetId}`;
      settings.settingsValue = JSON.stringify(widgetFilter);

      upsertUserConfigs.mutate(settings);
    },
    [filterKey, hostPathname, upsertUserConfigs, widgetId]
  );

  return { currentFilterValue, setFilter };
}
